// @givmo/app-admin — Company Fundraiser CREATE wizard (EP-WEB3).
//
// Drives the generated SDK through the create→update→publish lifecycle end-to-end (mirroring the
// consumer empty-draft-first wizard). The company create route only makes the DRAFT + stamps
// `owner_company_id`; everything else reuses the existing `/fundraisers/{id}/…` endpoints:
//
//   1. DETAILS   → `companiesCreateCompanyFundraiser` creates the DRAFT (name/description/goal in the
//                  body). Re-visiting details after the draft exists issues `fundraisersUpdateFundraiser`.
//                  The acting admin's personal 100-fundraiser cap (D-EP-12) is enforced by the backend
//                  on create; its error is surfaced HERE, on the details step, never swallowed.
//   2. COVER     → `fundraisersUploadFundraiserPicture` (optional; multipart, webp-friendly).
//   3. RECIPIENTS→ add/remove charities/ETFs/collections via the item sub-resource routes (≥1 required
//                  to publish — the backend enforces at publish; we gate Continue + surface its error).
//   4. REVIEW    → `fundraisersPublishFundraiser` (DRAFT→ACTIVE). On success → the manage page.
//                  EP-WEB15: REVIEW also carries the organization contact offer (D-FCM-15) whenever the
//                  draft's private read does; a changed choice is written in one PUT before the publish.
//
// DARK: reached only via the `/company-fundraisers/new` route, which redirects to /dashboard while
// `VITE_COMPANY_FUNDRAISERS_ENABLED` is off. Permission is Member-level — no Owner gate anywhere.

import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  companiesCreateCompanyFundraiserMutation,
  companiesGetCompanyAdminOptions,
  companiesGetCompanyOptions,
  companiesListCompanyFundraisersQueryKey,
  fundraisersGetOwnFundraiserOptions,
  fundraisersGetOwnFundraiserQueryKey,
  fundraisersPublishFundraiserMutation,
  fundraisersUpdateFundraiserMutation,
} from '@givmo/api-client/query';
import {
  isGivmoError,
  type CompanyFundraiserCreate,
  type FundraiserOut,
  type FundraiserUpdate,
} from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Icon,
  PageHeader,
  TextField,
  Textarea,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { ArrowLeft } from 'lucide-react';
import { registerAssistantInsertionTarget } from '@givmo/assistant-panel';
import {
  OrgContactOffer,
  contactOfferFromPrivateRead,
  orgContactProblem,
  orgContactRefusal,
  orgContactWrite,
} from '@givmo/fundraisers-module';
import {
  DESCRIPTION_MAX_LENGTH,
  NAME_MAX_LENGTH,
  currentRecipients,
  fundraiserGoal,
  goalUpdate,
  parseGoalDollars,
} from './company-fundraisers-decode';
import { CoverImageInput } from './cover-image-input';
import { RecipientPicker } from './recipient-picker';

const PREFIX = 'company-fundraiser-wizard';

type Step = 'details' | 'cover' | 'recipients' | 'review';
const STEP_ORDER: readonly Step[] = ['details', 'cover', 'recipients', 'review'];

export function CompanyFundraiserWizard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('details');
  const [draftId, setDraftId] = useState<string | null>(null);
  const [created, setCreated] = useState<FundraiserOut | null>(null);

  // Field state.
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [goalRaw, setGoalRaw] = useState('');
  const [goalError, setGoalError] = useState<string | null>(null);
  const [detailsError, setDetailsError] = useState<string | null>(null);

  // EP-AI1 — offer the description field to the assistant panel, the same registration the manage
  // page makes. No button is added to this page (D-EP-45); the panel's own prompts are the
  // affordance.
  //
  // ⚠ SCOPED TO STEP 1, because that is the only step where the field is on screen. Registered for
  // the whole wizard, the panel would offer "Insert into description" from the cover, recipients and
  // review steps — a control that writes into state the person cannot see and appears to do nothing.
  //
  // A conversation created anywhere in the wizard is scoped to the company rather than to a
  // fundraiser: the draft has no id until step 1 saves it, and `entryContextExtraForPath` excludes
  // the `new` route segment for that reason.
  useEffect(() => {
    if (step !== 'details') return;
    return registerAssistantInsertionTarget({
      actionLabel: 'Insert into description',
      insert: (text) => setDescription(text),
    });
  }, [step]);
  const [publishError, setPublishError] = useState<string | null>(null);

  // Once a draft exists, the manage-shaped detail is the source of truth for cover + items (kept fresh
  // by invalidating this query after each cover upload / item add-remove).
  const detailQuery = useQuery({
    ...fundraisersGetOwnFundraiserOptions({ path: { fundraiser_id: draftId ?? '' } }),
    enabled: draftId !== null,
  });
  const fundraiser: FundraiserOut | null = detailQuery.data ?? created;

  // EP-WEB15 — the organization contact offer (D-FCM-15), on REVIEW, where the draft exists: its private
  // read carries the offer only for a company's row with the backend switch on (no web switch). The
  // company read supplies the name and the domain (the byline's name if that read fails, so the offer
  // degrades rather than vanishing); the admin's own read, their address. The choice stays local until
  // Publish writes it.
  const company = useQuery(companiesGetCompanyOptions());
  const me = useQuery(companiesGetCompanyAdminOptions());
  const offer = contactOfferFromPrivateRead(fundraiser);
  const ownEmail = me.data?.email?.trim() || null;
  // The name the offer states: null while the company read is still loading (or if no name exists).
  const offerOrgName =
    offer !== null && !company.isPending
      ? (company.data?.name ?? fundraiser?.owner_company?.name ?? null)
      : null;
  const [offerShared, setOfferShared] = useState<boolean | null>(null);
  const [offerEmail, setOfferEmail] = useState<string | null>(null);
  const [offerError, setOfferError] = useState<string | null>(null);
  const shared = offerShared ?? offer?.shared ?? false;
  const contactEmail = offerEmail ?? offer?.email ?? ownEmail ?? '';
  const offerRule = {
    ownEmail,
    offeredEmail: offer?.email ?? null,
    domain: company.data?.domain?.trim() || null,
  };
  // Publish waits while the offer is about to appear, then only while sharing is ON with an unusable
  // address; sharing OFF never holds it back.
  const offerHoldsPublish =
    offer !== null &&
    (company.isPending ||
      (offerOrgName !== null && orgContactProblem(shared, contactEmail, offerRule) !== null));

  const refetchDraft = async () => {
    if (draftId === null) return;
    await queryClient.invalidateQueries({
      queryKey: fundraisersGetOwnFundraiserQueryKey({ path: { fundraiser_id: draftId } }),
    });
  };
  const invalidateList = () =>
    queryClient.invalidateQueries({ queryKey: companiesListCompanyFundraisersQueryKey() });

  const createDraft = useMutation({
    ...companiesCreateCompanyFundraiserMutation(),
    onSuccess: async (result: FundraiserOut) => {
      setDetailsError(null);
      setDraftId(result.id);
      setCreated(result);
      await invalidateList();
      setStep('cover');
    },
    onError: (error) => {
      // Surfaces the backend's cap error (D-EP-12: counts against the admin's personal 100) and any
      // validation error cleanly on the details step — never a silent failure.
      setDetailsError(
        isGivmoError(error)
          ? error.message
          : 'We couldn’t create the fundraiser. Please try again.',
      );
    },
  });

  const updateDraft = useMutation({
    ...fundraisersUpdateFundraiserMutation(),
    onSuccess: async () => {
      setDetailsError(null);
      await refetchDraft();
      setStep('cover');
    },
    onError: (error) => {
      setDetailsError(
        isGivmoError(error) ? error.message : 'We couldn’t save your changes. Please try again.',
      );
    },
  });

  const publish = useMutation({
    ...fundraisersPublishFundraiserMutation(),
    onSuccess: async () => {
      setPublishError(null);
      if (draftId === null) return;
      // The cover/recipients steps populated the own-fundraiser key with the DRAFT. Refresh it (and the
      // list) BEFORE navigating, and AWAIT it: under the 30s `staleTime` the manage page would otherwise
      // paint that cached draft — a "Draft" badge and a live "Publish fundraiser" card over a fundraiser
      // that is already ACTIVE. Invalidating without awaiting still flashes it for the round-trip, so the
      // await is load-bearing (same shape as the manage page's own publish).
      await refetchDraft();
      void invalidateList();
      toast.success('Fundraiser published.');
      void navigate({
        to: '/company-fundraisers/$fundraiserId',
        params: { fundraiserId: draftId },
      });
    },
    onError: (error) => {
      // Publish requires ≥1 recipient item; surface the backend's message rather than guessing.
      setPublishError(
        isGivmoError(error)
          ? error.message
          : 'We couldn’t publish the fundraiser. Please try again.',
      );
    },
  });

  const detailsBusy = createDraft.isPending || updateDraft.isPending;

  // The offer's own write, before the publish: one PUT carrying only what changed (switching on with
  // the offered address unchanged writes the switch alone, so the server resolves whose address it
  // is). A refusal lands on the field and nothing is published.
  const saveOffer = useMutation({ ...fundraisersUpdateFundraiserMutation() });
  const publishing = publish.isPending || saveOffer.isPending;
  async function publishDraft(fundraiserId: string) {
    setPublishError(null);
    setOfferError(null);
    const write = offer !== null ? orgContactWrite(shared, contactEmail, offer) : null;
    if (write !== null) {
      try {
        const saved = await saveOffer.mutateAsync({
          path: { fundraiser_id: fundraiserId },
          body: write,
        });
        // The answer is the draft as it now stands, so a retry after a failed publish sends nothing.
        queryClient.setQueryData(
          fundraisersGetOwnFundraiserQueryKey({ path: { fundraiser_id: fundraiserId } }),
          saved,
        );
      } catch (error) {
        const refusal = orgContactRefusal(error);
        if (refusal !== null) setOfferError(refusal);
        else
          setPublishError(
            isGivmoError(error)
              ? error.message
              : 'We couldn’t save your changes. Please try again.',
          );
        return;
      }
    }
    publish.mutate({ path: { fundraiser_id: fundraiserId } });
  }

  function submitDetails() {
    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      setDetailsError('Give your fundraiser a name.');
      return;
    }
    const goal = parseGoalDollars(goalRaw);
    if ('error' in goal) {
      setGoalError(goal.error);
      return;
    }
    setGoalError(null);
    setDetailsError(null);

    if (draftId === null) {
      // CREATE: omit unset optionals — there is no prior value to clear on a brand-new draft.
      const body: CompanyFundraiserCreate = {
        name: trimmedName,
        ...(description.trim().length > 0 ? { description: description.trim() } : {}),
        ...(goal.value !== null ? { goal_amount: goal.value } : {}),
      };
      createDraft.mutate({ body });
    } else {
      // UPDATE (the user came Back to details): the PUT is skip-None, so the description is sent
      // UNCONDITIONALLY (an emptied one is `""`, which clears it), and an emptied goal removes the
      // draft's saved goal with `remove_goal` (FCM-BE13), via `goalUpdate` — a null `goal_amount`
      // would KEEP the old goal while the review step reads as saved.
      const body: FundraiserUpdate = {
        name: trimmedName,
        description: description.trim(),
        ...goalUpdate(goal.value, fundraiser?.goal_amount),
      };
      updateDraft.mutate({ path: { fundraiser_id: draftId }, body });
    }
  }

  const recipientCount = fundraiser !== null ? currentRecipients(fundraiser).length : 0;

  return (
    <div
      className="mx-auto flex w-full max-w-[720px] flex-col gap-24 px-24 py-24"
      {...testIdProp('page-company-fundraiser-new')}
    >
      <PageHeader
        title="New Company Fundraiser"
        subtitle="Create a fundraiser your company runs on Givmo."
      />

      <Stepper step={step} draftId={draftId} />

      {step === 'details' && (
        <Card>
          <CardHeader title="Details" subtitle="Name your fundraiser and set an optional goal." />
          <CardBody className="flex flex-col gap-16">
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={NAME_MAX_LENGTH}
              placeholder="e.g. Acme gives back 2026"
              testId={`${PREFIX}-name`}
            />
            <Textarea
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={DESCRIPTION_MAX_LENGTH}
              rows={4}
              placeholder="Tell donors what this fundraiser supports (optional)."
              testId={`${PREFIX}-description`}
            />
            <TextField
              label="Goal (USD, optional)"
              inputMode="decimal"
              value={goalRaw}
              onChange={(e) => setGoalRaw(e.target.value)}
              error={goalError !== null}
              errorMessage={goalError ?? undefined}
              placeholder="500"
              className="max-w-[220px]"
              testId={`${PREFIX}-goal`}
            />
            {detailsError !== null && (
              <p
                className="text-s13 font-w500 text-danger-700"
                {...testIdProp(`${PREFIX}-details-error`)}
              >
                {detailsError}
              </p>
            )}
            <div className="flex items-center justify-end">
              <Button
                appearance="saas"
                tone="primary"
                size="lg"
                loading={detailsBusy}
                disabled={name.trim().length === 0 || detailsBusy}
                onClick={submitDetails}
                testId={`${PREFIX}-details-continue`}
              >
                {draftId === null ? 'Create draft & continue' : 'Save & continue'}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {step === 'cover' && fundraiser !== null && (
        <Card>
          <CardHeader
            title="Cover image"
            subtitle="Add a cover (optional). You can change it later."
          />
          <CardBody className="flex flex-col gap-16">
            <CoverImageInput
              fundraiserId={fundraiser.id}
              imageUrl={fundraiser.image_url}
              onUploaded={refetchDraft}
            />
            <StepNav
              onBack={() => setStep('details')}
              onNext={() => setStep('recipients')}
              nextLabel="Continue"
            />
          </CardBody>
        </Card>
      )}

      {step === 'recipients' && fundraiser !== null && (
        <Card>
          <CardHeader
            title="Recipients"
            subtitle="Choose where donations go. Add at least one to publish."
          />
          <CardBody className="flex flex-col gap-16">
            <RecipientPicker fundraiser={fundraiser} onChanged={refetchDraft} />
            <StepNav
              onBack={() => setStep('cover')}
              onNext={() => setStep('review')}
              nextLabel="Continue"
              nextDisabled={recipientCount === 0}
              nextTestId={`${PREFIX}-recipients-continue`}
            />
          </CardBody>
        </Card>
      )}

      {step === 'review' && fundraiser !== null && (
        <Card>
          <CardHeader
            title="Review & publish"
            subtitle="Publish makes the fundraiser live and donatable."
          />
          <CardBody className="flex flex-col gap-16">
            <ReviewRow label="Name" value={fundraiser.name} />
            <ReviewRow label="Goal" value={fundraiserGoal(fundraiser) ?? 'No goal set'} />
            <ReviewRow label="Recipients" value={`${recipientCount} selected`} />
            {offerOrgName !== null && (
              <OrgContactOffer
                orgName={offerOrgName}
                rule={offerRule}
                shared={shared}
                email={contactEmail}
                onSharedChange={(value) => {
                  setOfferShared(value);
                  setOfferError(null);
                }}
                onEmailChange={(value) => {
                  setOfferEmail(value);
                  setOfferError(null);
                }}
                serverError={offerError}
                disabled={publishing}
                testIdPrefix={PREFIX}
                className="pt-8"
              />
            )}
            {publishError !== null && (
              <p
                className="text-s13 font-w500 text-danger-700"
                {...testIdProp(`${PREFIX}-publish-error`)}
              >
                {publishError}
              </p>
            )}
            <StepNav
              onBack={() => setStep('recipients')}
              onNext={() => void publishDraft(fundraiser.id)}
              nextLabel="Publish"
              nextLoading={publishing}
              nextDisabled={recipientCount === 0 || publishing || offerHoldsPublish}
              nextTestId={`${PREFIX}-publish`}
            />
          </CardBody>
        </Card>
      )}
    </div>
  );
}

function Stepper({ step, draftId }: { step: Step; draftId: string | null }) {
  const currentIndex = STEP_ORDER.indexOf(step);
  const labels: Record<Step, string> = {
    details: 'Details',
    cover: 'Cover',
    recipients: 'Recipients',
    review: 'Review',
  };
  // A phone lane cannot hold all four labels: the row needs ~391px and a 390px phone leaves 342, so
  // it scrolled the whole page sideways on every step. Below `sm` only the CURRENT step keeps its
  // visible label; the others compact to their numbered marker and stay in the accessibility tree,
  // where `aria-current` says which step this is.
  //
  // An UPCOMING marker is a hairline disc on the surface fill. The stepper sits on the shell's
  // `surface-sunken` ground, so a sunken-filled disc had no edge and read as a bare numeral — and on
  // a phone the marker is all an upcoming step shows. The numeral also reaches AA on white (4.68:1).
  return (
    <ol
      className="flex items-center gap-8"
      {...testIdProp(`${PREFIX}-stepper`)}
      data-draft-id={draftId ?? ''}
    >
      {STEP_ORDER.map((s, i) => {
        const state = i < currentIndex ? 'done' : i === currentIndex ? 'current' : 'upcoming';
        return (
          <li
            key={s}
            className="flex items-center gap-8"
            {...testIdProp(`${PREFIX}-step-${s}`)}
            data-state={state}
            aria-current={state === 'current' ? 'step' : undefined}
          >
            <span
              className={
                state === 'current'
                  ? 'flex size-24 items-center justify-center rounded-full bg-brand-600 text-s12 font-w700 text-white'
                  : state === 'done'
                    ? 'flex size-24 items-center justify-center rounded-full bg-brand-100 text-s12 font-w700 text-brand-700'
                    : 'flex size-24 items-center justify-center rounded-full border border-border-strong bg-surface text-s12 font-w700 text-text-tertiary'
              }
            >
              {i + 1}
            </span>
            <span
              className={
                state === 'current'
                  ? 'text-s13 font-w600 text-text-strong'
                  : state === 'done'
                    ? 'text-s13 font-w600 text-text-strong max-sm:sr-only'
                    : 'text-s13 text-text-tertiary max-sm:sr-only'
              }
            >
              {labels[s]}
            </span>
            {i < STEP_ORDER.length - 1 && (
              <span className="h-px w-16 bg-border-subtle" aria-hidden />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function StepNav({
  onBack,
  onNext,
  nextLabel,
  nextDisabled,
  nextLoading,
  nextTestId,
}: {
  onBack: () => void;
  onNext: () => void;
  nextLabel: string;
  nextDisabled?: boolean;
  nextLoading?: boolean;
  nextTestId?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <Button
        appearance="saas"
        tone="ghost"
        size="md"
        leadingIcon={<Icon icon={ArrowLeft} size={14} aria-hidden />}
        onClick={onBack}
        testId={`${PREFIX}-back`}
      >
        Back
      </Button>
      <Button
        appearance="saas"
        tone="primary"
        size="lg"
        loading={nextLoading}
        disabled={nextDisabled}
        onClick={onNext}
        testId={nextTestId ?? `${PREFIX}-next`}
      >
        {nextLabel}
      </Button>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-border-subtle pb-8 last:border-0 last:pb-0">
      <span className="text-s13 text-text-tertiary">{label}</span>
      <span className="text-s14 font-w600 text-text-strong">{value}</span>
    </div>
  );
}
